/**
 * @fileoverview Notice Received Dialog
 * @module NoticeReceivedDialog
 * 
 * Business Context:
 * - Allows users to indicate they've received their Notice of Appraised Value
 * - Captures the notice date and appraised value from the county
 * - Unlocks Step 2 (File Your Appeal) when submitted
 * 
 * Technical Context:
 * - Modal dialog with form inputs
 * - Saves to Property model via apiClient.users.updateProperty
 */

import React, { useState, useCallback } from 'react';
import { apiClient } from '../../api/client';
import {
  X,
  Mail,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2
} from 'lucide-react';

interface NoticeReceivedDialogProps {
  isOpen: boolean;
  propertyId: string;
  currentAssessedValue?: number | null;
  onClose: () => void;
  onSuccess: (noticeDate: Date, assessedValue: number) => void;
}

export function NoticeReceivedDialog({
  isOpen,
  propertyId,
  currentAssessedValue,
  onClose,
  onSuccess
}: NoticeReceivedDialogProps) {
  const [step, setStep] = useState<'confirm' | 'details'>('confirm');
  const [noticeDate, setNoticeDate] = useState<string>('');
  const [assessedValue, setAssessedValue] = useState<string>(
    currentAssessedValue ? (currentAssessedValue / 100).toString() : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!noticeDate || !assessedValue) {
      setError('Please enter both the notice date and assessed value');
      return;
    }

    const valueInCents = Math.round(parseFloat(assessedValue.replace(/[^0-9.]/g, '')) * 100);
    if (isNaN(valueInCents) || valueInCents <= 0) {
      setError('Please enter a valid assessed value');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await apiClient.users.updateProperty(propertyId, {
        lastAppraisalNoticeDate: new Date(noticeDate).toISOString(),
        assessedValue: valueInCents
      });

      onSuccess(new Date(noticeDate), valueInCents);
    } catch (err: any) {
      console.error('Error saving notice info:', err);
      console.error('Full error response:', err?.response?.data);
      // Show validation details if available
      const errorDetails = err?.response?.data?.error?.details;
      const errorMessage = err?.response?.data?.error?.message || 'Failed to save. Please try again.';
      setError(errorDetails?.length ? `${errorMessage}: ${errorDetails.join(', ')}` : errorMessage);
    } finally {
      setSaving(false);
    }
  }, [noticeDate, assessedValue, propertyId, onSuccess]);

  const handleClose = useCallback(() => {
    setStep('confirm');
    setNoticeDate('');
    setAssessedValue(currentAssessedValue ? (currentAssessedValue / 100).toString() : '');
    setError(null);
    onClose();
  }, [currentAssessedValue, onClose]);

  if (!isOpen) return null;

  // Get today's date for max date validation
  const today = new Date().toISOString().split('T')[0];
  // Notices typically arrive in April, so start from beginning of year
  const minDate = `${new Date().getFullYear()}-01-01`;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'confirm' ? (
          <>
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Notice of Appraised Value</h2>
                    <p className="text-sm text-slate-500">Has your county notice arrived?</p>
                  </div>
                </div>
                <button 
                  onClick={handleClose}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-medium text-slate-900 mb-2">What is the Notice?</h3>
                <p className="text-sm text-slate-600">
                  Texas counties mail a <strong>Notice of Appraised Value</strong> (typically in April-May) 
                  showing your property's new assessed value. This triggers your deadline to file a protest.
                </p>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">Haven't received it yet?</h4>
                    <p className="text-sm text-amber-700">
                      Check your mail and your county's online portal. Notices are typically mailed in April-May.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  Not Yet
                </button>
                <button
                  onClick={() => setStep('details')}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Yes, I Got It
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Enter Notice Details</h2>
                    <p className="text-sm text-slate-500">From your Notice of Appraised Value</p>
                  </div>
                </div>
                <button 
                  onClick={handleClose}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-5">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Notice Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date on Notice
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    value={noticeDate}
                    onChange={(e) => setNoticeDate(e.target.value)}
                    min={minDate}
                    max={today}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  The date printed on your notice (usually top right corner)
                </p>
              </div>

              {/* Assessed Value */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Appraised/Assessed Value
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={assessedValue}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setAssessedValue(value ? parseInt(value).toLocaleString() : '');
                    }}
                    placeholder="e.g., 350,000"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  The "Market Value" or "Appraised Value" shown on your notice
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                <p className="text-sm text-teal-800">
                  <strong>Why we need this:</strong> Your protest deadline is calculated from the notice date. 
                  The assessed value helps us estimate your potential savings.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('confirm')}
                  disabled={saving}
                  className="px-4 py-3 border border-slate-200 rounded-lg text-slate-600 font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving || !noticeDate || !assessedValue}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Save & Unlock Step 2
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default NoticeReceivedDialog;
